import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, RotateCcw, Bot } from 'lucide-react';
import { botPlaygroundApi } from '../../services/api';

const optionLabel = (opt) => {
  if (opt == null) return '';
  if (typeof opt === 'string') return opt;
  return opt.title || opt.label || opt.description || opt.text || String(opt.id ?? '');
};

// Resolve the inline tokens the owner may have dropped into the opening message
// (#pictureN, #optionsN, #listN) into a renderable bubble: strip the tokens from
// the text and attach the first referenced image + options. Mirrors what the
// backend does for AI replies, so the preview's greeting behaves the same.
const resolveOpening = (text, pictures = [], interactives = [], bookingLink = null) => {
  let image_url = null;
  let options = null;

  const picByNum = new Map((pictures || []).map((p) => [Number(p.number), p]));
  const intByToken = new Map((interactives || []).map((i) => [i.token, i]));

  const clean = (text || '')
    .replace(/#picture(\d+)\b/g, (m, n) => {
      const pic = picByNum.get(Number(n));
      if (pic && !image_url) image_url = pic.image_url;
      return '';
    })
    .replace(/#(?:options|list)(\d+)\b/g, (m) => {
      const it = intByToken.get(m.trim());
      if (it && !options && it.items && it.items.length) {
        options = it.items.map((o) => ({ title: o.title, description: o.description }));
      }
      return '';
    })
    .replace(/#bookingLink(?:\{([^}]+)\})?/gi, (m, type) => {
      if (!bookingLink?.booking_url) return '';
      return type
        ? `${bookingLink.booking_url}?type=${encodeURIComponent(type.trim())}`
        : bookingLink.booking_url;
    })
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return { content: clean, image_url, options };
};

/**
 * A WhatsApp-style live preview that runs real messages through the store's AI
 * (via /bot-playground) using the current — possibly unsaved — settings passed
 * in `overrides`. Renders text, images and quick-reply options.
 */
const WhatsAppPreview = ({ overrides, businessName, isRTL, theme = 'dark', onMessagesChange, pictures = [], interactives = [], bookingLink = null }) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Let the parent (Tweak panel) read the current conversation.
  useEffect(() => {
    onMessagesChange?.(messages);
  }, [messages, onMessagesChange]);

  const opening = overrides?.opening_message?.trim();

  const seedOpening = useCallback(() => {
    if (opening) {
      const resolved = resolveOpening(opening, pictures, interactives, bookingLink);
      setMessages([{
        role: 'assistant',
        content: resolved.content,
        image_url: resolved.image_url,
        options: resolved.options,
      }]);
    } else {
      setMessages([]);
    }
  }, [opening, pictures, interactives, bookingLink]);

  // Seed the opening bubble once on mount (and when reset).
  useEffect(() => {
    seedOpening();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const sendText = useCallback(
    async (text) => {
      const trimmed = (text || '').trim();
      if (!trimmed || sending) return;

      const history = messages.map((m) => ({ role: m.role, content: m.content }));
      setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
      setInput('');
      setSending(true);

      try {
        const res = await botPlaygroundApi.send(trimmed, history, overrides || {});
        if (res.success && res.data) {
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: res.data.text || '',
              image_url: res.data.image_url || null,
              options: res.data.options || null,
            },
          ]);
        } else {
          setMessages((prev) => [
            ...prev,
            { role: 'assistant', content: res.error || t('botSettings.previewError', 'Something went wrong.') },
          ]);
        }
      } catch (e) {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: t('botSettings.previewError', 'Something went wrong.') },
        ]);
      } finally {
        setSending(false);
      }
    },
    [messages, overrides, sending, t]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    sendText(input);
  };

  const bgUrl = theme === 'light' ? '/wallpapers/wa-bg-light.jpg' : '/wallpapers/wa-bg-dark.png';

  return (
    <div className={`wa-preview ${isRTL ? 'rtl' : ''}`} data-theme={theme}>
      {/* Phone chrome header */}
      <div className="wa-preview-header">
        <div className="wa-preview-avatar">
          <Bot size={18} />
        </div>
        <div className="wa-preview-titles">
          <span className="wa-preview-name">{businessName || t('botSettings.previewBot', 'Your bot')}</span>
          <span className="wa-preview-status">{t('botSettings.previewOnline', 'online • preview')}</span>
        </div>
        <button
          type="button"
          className="wa-preview-reset"
          onClick={seedOpening}
          title={t('botSettings.previewReset', 'Restart chat')}
        >
          <RotateCcw size={16} />
        </button>
      </div>

      {/* Messages */}
      <div className="wa-preview-body" ref={scrollRef} style={{ backgroundImage: `url(${bgUrl})` }}>
        {messages.length === 0 && (
          <div className="wa-preview-hint">{t('botSettings.previewHint', 'Send a message to try your bot')}</div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`wa-bubble ${m.role === 'user' ? 'out' : 'in'}`}>
            {m.image_url && (
              <img className="wa-bubble-img" src={m.image_url} alt="" loading="lazy" />
            )}
            {m.content && <div className="wa-bubble-text">{m.content}</div>}
            {m.options && m.options.length > 0 && (
              <div className="wa-bubble-options">
                {m.options.map((opt, oi) => (
                  <button
                    key={oi}
                    type="button"
                    className="wa-option"
                    onClick={() => sendText(optionLabel(opt))}
                    disabled={sending}
                  >
                    {optionLabel(opt)}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
        {sending && (
          <div className="wa-bubble in wa-typing">
            <span></span>
            <span></span>
            <span></span>
          </div>
        )}
      </div>

      {/* Composer */}
      <form className="wa-preview-composer" onSubmit={handleSubmit}>
        <input
          type="text"
          className="wa-preview-input"
          placeholder={t('botSettings.previewPlaceholder', 'Type a message')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={sending}
        />
        <button type="submit" className="wa-preview-send" disabled={sending || !input.trim()}>
          <Send size={18} />
        </button>
      </form>
    </div>
  );
};

export default WhatsAppPreview;
